import { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  ChevronRight,
  Lock,
  ExternalLink,
  BookOpen,
  Headphones,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";
import "../../styles/supportCenter.css";

const QUICK_ACTIONS = [
  { label: "Login Page", path: "/login", desc: "Go to the BrixiGo sign-in page" },
  { label: "Reset Password", path: "/reset-password", desc: "Set a new password using a reset link" },
  { label: "My Profile", path: "/profile", desc: "View and update your profile information" },
  { label: "Security Settings", path: "/security", desc: "Change your password" },
  { label: "Users & Roles (Admin)", path: "/settings/security/users-roles", desc: "Manage users, roles, and access permissions" },
];

const LOGIN_ACCESS_TROUBLESHOOTING = [
  {
    problem: "Cannot log in — credentials are rejected",
    check: "BrixiGo has two login modes: Admin/HR login (username or work email + password) and Employee login (work email + password + organisation). Verify you are using the correct mode for your account type. Check that your username or email is typed correctly — admin login accepts a username (letters, numbers, underscore) or work email.",
    action: "Try logging in again with the correct mode selected. If using admin mode, try your work email instead of username (or vice versa). If using employee mode, ensure you have selected the correct organisation. If you have forgotten your password, use the password reset link.",
    path: "/login",
    pathLabel: "Login Page",
  },
  {
    problem: "Password reset link not working or expired",
    check: "The password reset page requires a valid token passed via the URL. If the link has expired or has already been used, it will not work. The new password must be at least 8 characters and both password fields must match.",
    action: "Request a new password reset link from your administrator. When setting the new password, ensure it is at least 8 characters long and that both the password and confirm password fields match exactly.",
    path: "/reset-password",
    pathLabel: "Reset Password",
  },
  {
    problem: "Logged in but redirected away from a page",
    check: "BrixiGo uses role-based access control. Pages are protected by role — for example, payroll and salary configuration pages require admin or HR roles, while employees can only access self-service pages (My Attendance, My Leave, Payslip, Holidays). If your role does not include access to a page, you will be redirected.",
    action: "Check your current role by going to My Profile — your role is displayed there. If you need access to a restricted page, contact your organisation's admin to update your role or page access permissions.",
    path: "/profile",
    pathLabel: "My Profile",
  },
  {
    problem: "Employee cannot access self-service pages (My Attendance, Leave, Payslip)",
    check: "Employee self-service pages are available for users with the 'employee' role. These pages include My Attendance, My Leave, Payslip, and Holidays. If the employee's role is not set correctly in their employee record, they may not see these pages in the sidebar.",
    action: "Ask an admin to verify the employee's System Access Role is set to 'employee' (or the appropriate role) in the employee record. The admin can check this in the employee edit wizard (Step 2 — Organisation). Also verify that page access permissions include the self-service pages.",
    path: "/settings/security/users-roles",
    pathLabel: "Users & Roles",
  },
  {
    problem: "Admin cannot access Users & Roles or IAM settings",
    check: "The Users & Roles page (IAM Access Management) is restricted to admin-only access. If your account role is hr, manager, finance, or employee, you will not be able to access this page.",
    action: "Verify your role is 'admin' by checking My Profile. If you are not an admin, contact the system administrator to either upgrade your role or perform the required user/role management on your behalf.",
    path: "/profile",
    pathLabel: "My Profile",
  },
  {
    problem: "User invited but cannot log in",
    check: "When a user is invited through the IAM invite wizard, they receive login credentials. If the invited user cannot log in, their account may not have been fully set up, the email address may be incorrect, or the password may need to be reset.",
    action: "Ask the admin to go to Users & Roles and verify the invited user's record — check their email, role, and status. If needed, trigger a new password reset for the user.",
    path: "/settings/security/users-roles",
    pathLabel: "Users & Roles",
  },
  {
    problem: "Session expired — forced to log in again",
    check: "BrixiGo uses token-based authentication. If your session token has expired, you will be logged out automatically and redirected to the login page. This is normal security behavior.",
    action: "Log in again with your credentials. If this happens frequently, check your network connection. If the issue persists across multiple users, contact your admin.",
    path: "/login",
    pathLabel: "Login Page",
  },
  {
    problem: "Page access permissions changed but sidebar still shows old items",
    check: "When an admin updates page access permissions via Users & Roles, the change takes effect on the user's next session load. The sidebar reflects the permissions fetched at login time.",
    action: "Ask the affected user to log out and log back in. This refreshes their session and reloads the updated page access permissions from the server.",
    path: "/login",
    pathLabel: "Login Page",
  },
  {
    problem: "Cannot change password from the Security page",
    check: "The Security page allows you to change your password by entering your current password and a new password. The new password must score at least 'Good' on the strength meter (minimum: 8 characters, mix of uppercase, lowercase, digits, and special characters). Both new password fields must match.",
    action: "Go to Security Settings, enter your current password, then enter a strong new password in both fields. Ensure the strength meter shows at least 'Good' before saving.",
    path: "/security",
    pathLabel: "Security Settings",
  },
];

export default function LoginAccessSupportPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [openItem, setOpenItem] = useState(null);
  const resultsRef = useRef(null);

  const q = search.trim().toLowerCase();

  const filteredItems = useMemo(() => {
    if (!q) return LOGIN_ACCESS_TROUBLESHOOTING;
    return LOGIN_ACCESS_TROUBLESHOOTING.filter(
      (t) =>
        t.problem.toLowerCase().includes(q) ||
        t.check.toLowerCase().includes(q) ||
        t.action.toLowerCase().includes(q)
    );
  }, [q]);

  return (
    <div className="brix-page">
      {/* ── Header ── */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-slate">
              <Lock size={28} strokeWidth={1.8} />
            </div>
            <div>
              <h1>Login / Access</h1>
              <p className="hs-hero-sub">
                Troubleshoot login, authentication, and access permission issues.
              </p>
            </div>
          </div>

          <form
            className="hs-search-wrap"
            onSubmit={(e) => {
              e.preventDefault();
              if (q && resultsRef.current) resultsRef.current.scrollIntoView({ behavior: "smooth" });
            }}
          >
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search login issues — e.g. password, access, role, permission..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      <div ref={resultsRef}>
        {/* ── Quick Actions ── */}
        <div style={{ marginBottom: 28 }}>
          <div className="hs-section-head">
            <h2 className="hs-section-title">Quick Actions</h2>
            <p className="hs-section-sub">Jump to the relevant page to check or fix your issue</p>
          </div>
          <div className="sc-issues-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
            {QUICK_ACTIONS.map((link) => (
              <div
                key={link.path}
                className="sc-issue-card"
                role="button"
                tabIndex={0}
                style={{ cursor: "pointer" }}
                onClick={() => navigate(link.path)}
                onKeyDown={(e) => e.key === "Enter" && navigate(link.path)}
              >
                <div className="sc-issue-name" style={{ fontSize: "0.88rem" }}>{link.label}</div>
                <div className="sc-issue-desc" style={{ fontSize: "0.78rem" }}>{link.desc}</div>
                <div className="sc-issue-link">
                  Open <ExternalLink size={12} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Login & Access Troubleshooting (main section) ── */}
        <div style={{ marginBottom: 28 }}>
          <div className="hs-section-head">
            <h2 className="hs-section-title">Login & Access Troubleshooting</h2>
            <p className="hs-section-sub">
              Select your issue to see what to check and how to resolve it
            </p>
          </div>

          {q && filteredItems.length === 0 && (
            <div className="sc-no-results">
              No login or access issues match &ldquo;<strong>{search}</strong>&rdquo;. Try a different keyword.
            </div>
          )}

          <div className="sc-accordion-list">
            {filteredItems.map((item, idx) => {
              const isOpen = openItem === idx;
              return (
                <div key={item.problem} className="hs-faq-item">
                  <button
                    type="button"
                    className="hs-faq-btn"
                    onClick={() => setOpenItem(isOpen ? null : idx)}
                  >
                    <span className="hs-faq-q">{item.problem}</span>
                    <ChevronDown size={16} className={`hs-faq-chevron${isOpen ? " open" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="hs-faq-answer">
                          <div style={{ marginBottom: 10 }}>
                            <strong style={{ color: "#64748b", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Check
                            </strong>
                            <p style={{ margin: "4px 0 0" }}>{item.check}</p>
                          </div>
                          <div style={{ marginBottom: item.path ? 12 : 0 }}>
                            <strong style={{ color: "#15803d", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Next Action
                            </strong>
                            <p style={{ margin: "4px 0 0" }}>{item.action}</p>
                          </div>
                          {item.path && (
                            <button
                              type="button"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "6px 14px",
                                fontSize: "0.82rem",
                                fontWeight: 500,
                                color: "#1a437d",
                                background: "rgba(26,67,125,0.06)",
                                border: "1px solid rgba(26,67,125,0.15)",
                                borderRadius: 6,
                                cursor: "pointer",
                              }}
                              onClick={() => navigate(item.path)}
                            >
                              {item.pathLabel}
                              <ExternalLink size={13} />
                            </button>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Need detailed login or access information? ── */}
        <div className="sc-col-card" style={{ marginBottom: 28 }}>
          <div className="sc-info-row">
            <div className="sc-col-header-icon sc-icon-blue">
              <BookOpen size={18} strokeWidth={2} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 className="hs-section-title">Need detailed login or access information?</h2>
              <p className="sc-info-row-desc">
                Visit the Getting Started Help Guide for detailed documentation on authentication, user setup, and access configuration.
              </p>
            </div>
            <button
              type="button"
              className="sc-info-row-btn"
              onClick={() => navigate("/help/getting-started")}
            >
              Open Getting Started Help Guide
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* ── Still facing an issue? ── */}
        <div className="sc-contact-card" style={{ marginBottom: 28 }}>
          <div className="sc-contact-inner sc-contact-row">
            <div className="sc-contact-icon">
              <Headphones size={24} strokeWidth={1.5} />
            </div>
            <div className="sc-contact-text">
              <h2 className="sc-contact-title">Still facing an issue?</h2>
              <p className="sc-contact-sub">
                If the available troubleshooting steps don't resolve the problem, contact our support team for further assistance.
              </p>
            </div>
            <button
              type="button"
              className="sc-contact-btn"
              onClick={() => navigate("/support")}
            >
              <Headphones size={15} />
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
